import { useState, useEffect } from 'react';
import { GameState, Player, CardData } from '../types/GameTypes';
import socketService from '../services/socketService';
import { MessageType } from '../services/WebSocketMessageHandler';

interface UseGameStateReturn {
  playerId: string | null;
  gameId: string | null;
  lobbyPlayers: Player[];
  gameState: GameState | null;
  marketCards: CardData[];
  handleFindGame: () => void;
  handleCancelFindGame: () => void;
  handlePlayCard: (cardId: string) => void;
}

export const useGameState = (userToken: string | null): UseGameStateReturn => {
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [gameId, setGameId] = useState<string | null>(null);
  const [lobbyPlayers, setLobbyPlayers] = useState<Player[]>([]);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [marketCards, setMarketCards] = useState<CardData[]>([]);

  useEffect(() => {
    if (!userToken) return;

    socketService.connect('wss://vikings.tenb.uk', userToken);
    socketService.connect('wss://vikings.tenb.uk/ws/game', userToken);

    const handlePlayerId = (message: any) => {
      setPlayerId(message.playerId);
    };

    const handleMatchFound = (message: any) => {
      setGameId(message.gameId);
      setLobbyPlayers(message.players);
      setGameState(message.gameState);
    };

    const handleGameStateUpdate = (message: any) => {
      setGameState(message.gameState);
    };

    // Register message handlers
    socketService.addMessageHandler(MessageType.PLAYER_ID, handlePlayerId);
    socketService.addMessageHandler(MessageType.MATCH_FOUND, handleMatchFound);
    socketService.addMessageHandler(MessageType.GAME_STATE_UPDATE, handleGameStateUpdate);

    return () => {
      socketService.removeMessageHandler(MessageType.PLAYER_ID, handlePlayerId);
      socketService.removeMessageHandler(MessageType.MATCH_FOUND, handleMatchFound);
      socketService.removeMessageHandler(MessageType.GAME_STATE_UPDATE, handleGameStateUpdate);
      socketService.disconnect();
    };
  }, [userToken]);

  const handleFindGame = () => {
    socketService.joinQueue();
  };

  const handleCancelFindGame = () => {
    socketService.leaveQueue();
  };

  const handlePlayCard = (cardId: string) => {
    if (gameId) {
      socketService.playCard(gameId, cardId);
    }
  };

  return {
    playerId,
    gameId,
    lobbyPlayers,
    gameState,
    marketCards,
    handleFindGame,
    handleCancelFindGame,
    handlePlayCard,
  };
};
